import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle,
  FileText,
  MapPin,
  Phone,
  MessageCircle,
  ArrowRight,
  Loader2,
  ScrollText,
  Mail
} from "lucide-react";

export default function OnboardingFlow({ onComplete }) {
  const { user, token, updateUser } = useAuth();
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(null);
  
  // Terms dialog state
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [termsContent, setTermsContent] = useState("");
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [acceptingTerms, setAcceptingTerms] = useState(false);
  const termsScrollRef = useRef(null);
  
  // Additional info dialog state
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [submittingInfo, setSubmittingInfo] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState({
    city: "",
    street: "",
    zip_code: "",
    phone: "",
    phone_area_code: "",
    whatsapp: "",
    telegram: ""
  });
  
  // Email verification dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API}/v2/users/onboarding/status`, { headers });
      setOnboardingStatus(response.data);
      
      const nextStep = response.data.next_step;
      setCurrentStep(nextStep);
      
      // Auto-open dialogs based on next step
      if (nextStep === "verify_email") {
        setShowEmailDialog(true);
      } else if (nextStep === "accept_terms") {
        fetchTermsContent();
        setShowTermsDialog(true);
      } else if (nextStep === "additional_info") {
        setShowInfoDialog(true);
      } else if (nextStep === "completed" || response.data.user_status === "active") {
        onComplete?.();
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTermsContent = async () => {
    try {
      const response = await axios.get(`${API}/terms-of-use`);
      setTermsContent(response.data.content);
    } catch (error) {
      console.error("Error fetching terms:", error);
      toast.error("Failed to load terms of use");
    }
  };

  const handleTermsScroll = (e) => {
    const element = e.target;
    const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcceptTerms = async () => {
    if (!hasScrolledToBottom) {
      toast.error("Please scroll through and read the entire terms of use");
      return;
    }
    
    if (!termsAccepted) {
      toast.error("Please check the box to accept the terms");
      return;
    }

    setAcceptingTerms(true);
    try {
      const response = await axios.post(
        `${API}/v2/users/onboarding/accept-terms`,
        { accepted: true },
        { headers }
      );
      
      updateUser(response.data.user);
      toast.success("Terms accepted!");
      setShowTermsDialog(false);
      
      // Move to next step
      setCurrentStep("additional_info");
      setShowInfoDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept terms");
    } finally {
      setAcceptingTerms(false);
    }
  };

  const handleInfoChange = (field, value) => {
    setAdditionalInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitInfo = async () => {
    // Validate
    if (!additionalInfo.city || !additionalInfo.street || !additionalInfo.zip_code) {
      toast.error("Please fill in all address fields");
      return;
    }
    
    if (!additionalInfo.phone || !additionalInfo.phone_area_code) {
      toast.error("Please provide your phone number with area code");
      return;
    }
    
    if (!additionalInfo.whatsapp && !additionalInfo.telegram) {
      toast.error("Please provide at least WhatsApp or Telegram contact");
      return;
    }

    setSubmittingInfo(true);
    try {
      const response = await axios.post(
        `${API}/v2/users/onboarding/additional-info`,
        additionalInfo,
        { headers }
      );
      
      updateUser(response.data.user);
      toast.success("Information submitted! Your account is pending approval.");
      setShowInfoDialog(false);
      setCurrentStep("pending_approval");
      onComplete?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit information");
    } finally {
      setSubmittingInfo(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // If user has completed onboarding or is active, don't show anything
  if (currentStep === "completed" || onboardingStatus?.user_status === "active") {
    return null;
  }

  // If pending approval, show status
  if (currentStep === "pending_approval" || onboardingStatus?.onboarding_status === "info_completed") {
    return null; // The dashboard will show pending status
  }

  return (
    <>
      {/* Terms of Use Dialog - Mobile optimized */}
      <Dialog open={showTermsDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl max-h-[90vh] w-[calc(100%-2rem)] sm:w-full flex flex-col mx-auto" hideCloseButton>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <ScrollText className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              Terms of Use
            </DialogTitle>
            <DialogDescription className="text-sm">
              Please read and accept the terms of use to continue
            </DialogDescription>
          </DialogHeader>
          
          <div 
            ref={termsScrollRef}
            onScroll={handleTermsScroll}
            className="flex-1 overflow-y-auto border rounded-lg p-3 sm:p-4 bg-slate-50 text-xs sm:text-sm text-slate-700 whitespace-pre-wrap max-h-[300px] sm:max-h-[400px]"
            data-testid="terms-content"
          >
            {termsContent || "Loading terms..."}
          </div>
          
          {!hasScrolledToBottom && (
            <p className="text-xs sm:text-sm text-amber-600 flex items-center gap-2">
              <ArrowRight className="w-4 h-4 animate-bounce" />
              Please scroll down to read the entire terms
            </p>
          )}
          
          <div className="flex items-center gap-2 sm:gap-3 pt-3 sm:pt-4 border-t">
            <Checkbox
              id="accept-terms"
              checked={termsAccepted}
              onCheckedChange={setTermsAccepted}
              disabled={!hasScrolledToBottom}
              className="h-5 w-5"
              data-testid="terms-checkbox"
            />
            <Label 
              htmlFor="accept-terms" 
              className={`text-xs sm:text-sm cursor-pointer ${!hasScrolledToBottom ? 'text-slate-400' : 'text-slate-700'}`}
            >
              I have read and agree to the Terms of Use
            </Label>
          </div>
          
          <Button
            onClick={handleAcceptTerms}
            disabled={!hasScrolledToBottom || !termsAccepted || acceptingTerms}
            className="w-full h-11"
            data-testid="accept-terms-btn"
          >
            {acceptingTerms ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Accepting...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                I Accept
              </>
            )}
          </Button>
        </DialogContent>
      </Dialog>

      {/* Additional Info Dialog - Mobile optimized */}
      <Dialog open={showInfoDialog} onOpenChange={() => {}}>
        <DialogContent className="max-w-lg w-[calc(100%-2rem)] sm:w-full mx-auto max-h-[90vh] overflow-y-auto" hideCloseButton>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
              You're Almost There!
            </DialogTitle>
            <DialogDescription className="text-sm">
              Please provide your address and contact information to complete registration
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-3 sm:py-4">
            {/* Address Section */}
            <div className="space-y-3">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 text-sm sm:text-base">
                <MapPin className="w-4 h-4" />
                Address Information
              </h3>
              
              <div>
                <Label htmlFor="street" className="text-sm">Street Address *</Label>
                <Input
                  id="street"
                  value={additionalInfo.street}
                  onChange={(e) => handleInfoChange("street", e.target.value)}
                  placeholder="123 Main Street, Apt 4B"
                  className="h-11"
                  data-testid="info-street-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <Label htmlFor="city" className="text-sm">City *</Label>
                  <Input
                    id="city"
                    value={additionalInfo.city}
                    onChange={(e) => handleInfoChange("city", e.target.value)}
                    placeholder="New York"
                    className="h-11"
                    data-testid="info-city-input"
                  />
                </div>
                <div>
                  <Label htmlFor="zip_code" className="text-sm">ZIP Code *</Label>
                  <Input
                    id="zip_code"
                    value={additionalInfo.zip_code}
                    onChange={(e) => handleInfoChange("zip_code", e.target.value)}
                    placeholder="10001"
                    className="h-11"
                    data-testid="info-zip-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Phone Section */}
            <div className="space-y-3 pt-2 border-t">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 text-sm sm:text-base">
                <Phone className="w-4 h-4" />
                Phone Number
              </h3>
              
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <div>
                  <Label htmlFor="phone_area_code" className="text-sm">Code *</Label>
                  <Input
                    id="phone_area_code"
                    value={additionalInfo.phone_area_code}
                    onChange={(e) => handleInfoChange("phone_area_code", e.target.value)}
                    placeholder="+1"
                    className="h-11"
                    data-testid="info-area-code-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="phone" className="text-sm">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={additionalInfo.phone}
                    onChange={(e) => handleInfoChange("phone", e.target.value)}
                    placeholder="555-123-4567"
                    className="h-11"
                    data-testid="info-phone-input"
                  />
                </div>
              </div>
            </div>
            
            {/* Messaging Apps Section */}
            <div className="space-y-3 pt-2 border-t">
              <h3 className="font-medium text-slate-900 flex items-center gap-2 text-sm sm:text-base">
                <MessageCircle className="w-4 h-4" />
                Messaging Contact (at least one)
              </h3>
              
              <div>
                <Label htmlFor="whatsapp" className="text-sm">WhatsApp Number</Label>
                <Input
                  id="whatsapp"
                  value={additionalInfo.whatsapp}
                  onChange={(e) => handleInfoChange("whatsapp", e.target.value)}
                  placeholder="+1 555-123-4567"
                  className="h-11"
                  data-testid="info-whatsapp-input"
                />
              </div>
              
              <div>
                <Label htmlFor="telegram" className="text-sm">Telegram Username</Label>
                <Input
                  id="telegram"
                  value={additionalInfo.telegram}
                  onChange={(e) => handleInfoChange("telegram", e.target.value)}
                  placeholder="@yourusername"
                  className="h-11"
                  data-testid="info-telegram-input"
                />
              </div>
            </div>
          </div>
          
          <Button
            onClick={handleSubmitInfo}
            disabled={submittingInfo}
            className="w-full h-11"
            data-testid="submit-info-btn"
          >
            {submittingInfo ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <ArrowRight className="w-4 h-4 mr-2" />
                Complete Registration
              </>
            )}
          </Button>
        </DialogContent>
      </Dialog>
      
      {/* Email Verification Required Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md w-[calc(100%-2rem)] sm:w-full mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              Add Your Gmail Address
            </DialogTitle>
            <DialogDescription className="text-sm">
              To get started, you need to register at least one Gmail address
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Why do I need to add an email?</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• You'll receive task notifications at this email</li>
                <li>• Complete email-based tasks to earn points</li>
                <li>• Add up to 10 Gmail addresses to maximize earnings</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="font-medium text-amber-900 mb-2">Setup Steps:</h4>
              <ol className="text-sm text-amber-800 space-y-1">
                <li>1. Click "Add Email" below</li>
                <li>2. Enter your Gmail address</li>
                <li>3. Verify it via the confirmation email</li>
                <li>4. Return here to continue setup</li>
              </ol>
            </div>
            
            <Button 
              className="w-full"
              onClick={() => {
                setShowEmailDialog(false);
                // Scroll to the "Add Email" section
                const addEmailBtn = document.querySelector('[data-testid="add-email-btn"]');
                if (addEmailBtn) {
                  addEmailBtn.scrollIntoView({ behavior: 'smooth' });
                  addEmailBtn.click();
                }
              }}
              data-testid="add-email-onboarding-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Add Gmail Address
            </Button>
            
            <p className="text-xs text-slate-500 text-center">
              After adding and verifying your email, refresh the page to continue
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
